import { usePermissions } from '@/hooks/usePermissions';
import { Navigate, useLocation } from 'react-router-dom';
import { ReactNode, useMemo } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredRole?: 'admin' | 'manager' | 'employee';
}

export const ProtectedRoute = ({ 
  children, 
  requiredPermission,
  requiredRole 
}: ProtectedRouteProps) => {
  const { hasPermission, userRole, loading, user } = usePermissions();
  const location = useLocation();
  
  // ProtectedRoute render: pathname, requiredPermission, requiredRole, userRole, loading, hasUser

  // Memoize the access checks to prevent infinite re-renders
  const accessChecks = useMemo(() => {
    const checks = {
      isLoading: loading,
      isAuthenticated: !!user,
      hasRequiredRole: !requiredRole || userRole === requiredRole || userRole === 'admin',
      hasRequiredPermission: !requiredPermission || hasPermission(requiredPermission)
    };
    
    // Access checks computed
    return checks;
  }, [loading, user, userRole, requiredRole, requiredPermission, hasPermission]);

  if (accessChecks.isLoading) {
    // ProtectedRoute: Still loading...
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!accessChecks.isAuthenticated) {
    // ProtectedRoute: Not authenticated, redirecting to /auth
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!accessChecks.hasRequiredRole) {
    // ProtectedRoute: Role check failed, redirecting to /dashboard
    return <Navigate to="/dashboard" replace />;
  }

  if (!accessChecks.hasRequiredPermission) {
    // ProtectedRoute: Permission check failed, redirecting to /dashboard
    return <Navigate to="/dashboard" replace />;
  }

  // ProtectedRoute: All checks passed, rendering children
  return <>{children}</>;
};